import { DateTime } from 'luxon';

import { otherFieldDateUnsetValue } from '~/common/config/constants';
import { MessagePayloadType, UserMessageType } from '~/common/types/messages';
import { PcfraDocumentType } from '~/common/types/pcfra';
import { HouseholdMemberUpdate, RiskFactorKey, TenantUpdate } from '~/common/types/people';
import { getRevokedPcfraDocumentStorePatch } from '~/common/utils/pcfra';
import { fullNameHistoricSuffix, getPersonRiskFactorItemId } from '~/common/utils/people';

import type { ClientUserStore } from './ClientUserStore';

export type ClientUserMessageHandlers = {
  [Type in UserMessageType]: ClientUserMessageHandlerFn<Type>;
};

export type ClientUserMessageHandlerFn<TMessageType extends UserMessageType> = (
  payload: MessagePayloadType<TMessageType>,
) => void;

const updatePerson = (
  userStore: ClientUserStore,
  personUpdate: TenantUpdate | HouseholdMemberUpdate,
) => {
  userStore.store.transaction(() => {
    userStore.patch('people', personUpdate);
  });
};

export const clientUserMessageHandlers = (
  userStore: ClientUserStore,
): ClientUserMessageHandlers => ({
  updatePropertyAttributes: (update) =>
    userStore.patch(update.isPcfra ? 'pcfraPropertyDetails' : 'propertyDetails', update),

  logVisit: (visit) => userStore.put('visits', visit),

  deleteVisitLog: ({ visitId }) => userStore.delete('visits', visitId),

  editVisitLog: (visit) => userStore.patch('visits', visit),

  updateTenant: (tenantUpdate) => updatePerson(userStore, tenantUpdate),

  updateHouseholdMember: (memberUpdate) => updatePerson(userStore, memberUpdate),

  createHouseholdMember: ({ values: { contactDetails, ...newMember } }) =>
    userStore.store.transaction(() => {
      userStore.put('people', newMember);
      userStore.replaceByCellValue(
        'contactDetails',
        'personId',
        newMember.personId,
        contactDetails,
      );
    }),

  endHouseholdMember: ({ personId, endDate = DateTime.now().toISODate() }) => {
    const { fullName } = userStore.get('people', personId) || {};

    userStore.patch('people', {
      personId,
      fullName: `${fullName} ${fullNameHistoricSuffix}`,
      isHistoric: true,
      householdMemberEnded: endDate,
    });
  },

  updateContactDetails: ({ personId, contactDetails }) =>
    userStore.replaceByCellValue('contactDetails', 'personId', personId, contactDetails),

  createCase: (newCase) => userStore.put('cases', newCase),

  createCaseEvent: (event) => userStore.put('caseEvents', event),

  closeCase: ({ caseId, closeReason }) =>
    userStore.patch('cases', { caseId, closeReason, isClosed: true }),

  createNote: (newNote) => userStore.put('notes', newNote),

  updatePcfraStatus: ({ personId, newStatus, comments }) =>
    userStore.patch('pcfraPeople', {
      personId,
      pcfraStatus: newStatus,
      pcfraStatusComments: comments,
    }),

  updateRpeepStatus: ({ personId, newStatus }) =>
    userStore.patch('pcfraPeople', {
      personId,
      rpeepStatus: newStatus,
    }),

  updatePcfraReviewDate: ({ personId, newDate }) =>
    userStore.patch('pcfraPeople', {
      personId,
      pcfraReviewDate: newDate === otherFieldDateUnsetValue ? '' : newDate,
    }),

  updatePcfraSelfReferred: ({ personId, propertyId, isSelfReferred }) => {
    const key: RiskFactorKey = 'selfReferredForPCFRA';

    userStore.put('peopleRiskFactors', {
      itemId: getPersonRiskFactorItemId(personId, key),
      propertyId,
      personId,
      key,
      value: isSelfReferred ? 'Y' : 'N',
      description: '',
    });
  },

  updatePcfraReferrer: (referrer) => userStore.put('pcfraReferrers', referrer),

  deletePcfraReferrer: ({ personId }) => userStore.delete('pcfraReferrers', personId),

  savePcfraDraft: (draft) => userStore.put('pcfraDrafts', draft),

  deletePcfraDraft: ({ draftId }) => userStore.delete('pcfraDrafts', draftId),

  revokePcfra: ({ personId }) => {
    const activeDoc = userStore
      .getByCellValue('pcfraDocuments', 'personId', personId)
      .find(({ documentType, isActive }) => documentType === PcfraDocumentType.Pcfra && isActive);

    if (activeDoc) {
      userStore.patch('pcfraDocuments', getRevokedPcfraDocumentStorePatch(activeDoc));
    }
  },

  revokeRpeep: ({ personId }) => {
    const activeDoc = userStore
      .getByCellValue('pcfraDocuments', 'personId', personId)
      .find(({ documentType, isActive }) => documentType === PcfraDocumentType.Rpeep && isActive);

    if (activeDoc) {
      userStore.patch('pcfraDocuments', getRevokedPcfraDocumentStorePatch(activeDoc));
    }
  },

  createFireSafetyNote: (note) => userStore.put('fireSafetyNotes', note),
});
